import React from 'react';
import { useRef, useState } from 'react';

import {
  BookmarkCheck01Icon,
  Delete02Icon,
  DeliveryReturn02Icon,
  Edit03Icon,
} from '@hugeicons/core-free-icons';
import cn from 'classnames';
import {
  fieldsFromNote,
  getAdminNoteTemplate,
  intercomConversationUrl,
} from 'constants/admin-note-templates';
import { useTranslation } from 'react-i18next';

import { ActionsMenu } from 'components/ActionsMenu/ActionsMenu';
import { AdminNoteForm } from 'components/AdminNoteForm/AdminNoteForm';
import { CommentBox } from 'components/CommentBox/CommentBox';
import { IconComponent } from 'components/Icon/IconComponent';
import StatusPill from 'components/Pills/StatusPills';
import { UserInfo } from 'components/UserInfo/UserInfo';

export const Comments = ({
  title,
  superText,
  caption,
  className,
  tooltip,
  initials,
  comment,
  tag,
  tagColor,
  tagValue,
  edited = false,
  tagOptions = [],
  allowDelete = false,
  allowEdit = false,
  maxLength = 255,
  onEdit = () => {},
  onDelete = () => {},
  allowResolve = false,
  onResolve = () => {},
  resolved = false,
  allowActions = true,
  onReopen = () => {},
  metadata = null,
  checkTypeSlug,
  notes = [],
  noteId,
}) => {
  const { t } = useTranslation();
  const actions = [
    {
      title: 'Edit',
      icon: Edit03Icon,
      onClick: () => handleEdit(),
      hide: !allowEdit,
      disabled: false,
      type: 'secondary',
    },
    {
      title: 'Reopen',
      icon: DeliveryReturn02Icon,
      onClick: () => onReopen(),
      hide: !allowResolve || !resolved,
      disabled: false,
      type: 'secondary',
    },
    {
      title: 'Delete',
      icon: Delete02Icon,
      onClick: () => onDelete(),
      hide: !allowDelete,
      disabled: false,
      type: 'error',
    },
  ];
  const [isEditing, setIsEditing] = useState(false);
  const commentBoxRef = useRef(null);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const updateSupportUserNote = payload => {
    onEdit(payload);
    setIsEditing(false);
  };

  const template = getAdminNoteTemplate(tagValue);
  const intercomUrl = intercomConversationUrl(metadata?.intercom_id);

  return (
    <div className={cn('d-flex flex-column u-gap', { [className]: className })}>
      <div className="d-flex align-items-start justify-content-between">
        <div className="d-flex align-items-start u-gap-medium flex-wrap">
          <UserInfo
            title={title}
            superText={superText}
            caption={caption}
            initials={initials}
            tooltip={tooltip || title}
          />
          <div className="d-flex align-items-center u-gap">
            {tag && <StatusPill className="u-margin-right--small" text={tag} status={tagColor} />}
            {metadata?.shared && (
              <StatusPill
                className="u-margin-right--small"
                text="Visible to client"
                status="neutral"
              />
            )}
            {edited && <span className="u-font-size-x-small tertiary-text">Edited</span>}
          </div>
        </div>
        <div className="d-flex align-items-center u-gap-small">
          {allowResolve && resolved && (
            <IconComponent
              icon={BookmarkCheck01Icon}
              type="success"
              noBackground
              size="small"
              className="u-margin-right--small"
              tip="Resolved"
            />
          )}
          {allowResolve && !resolved && (
            <button
              className="u-font-size-x-small secondary-text u-border surface-white u-border-radius--x-small"
              onClick={onResolve}>
              Resolve
            </button>
          )}
          {allowActions && (
            <ActionsMenu newIconLibrary={true} small>
              {actions.map((action, index) =>
                action.hide ? null : (
                  <ActionsMenu.Item
                    newIconLibrary={true}
                    key={action.title + index}
                    icon={action.icon}
                    title={action.title}
                    onClick={() => action.onClick()}
                    disabled={action.disabled}
                    type={action.type}
                  />
                ),
              )}
            </ActionsMenu>
          )}
        </div>
      </div>
      {isEditing ? (
        template ? (
          <AdminNoteForm
            tagDefault={tagValue}
            checkTypeSlug={checkTypeSlug}
            notes={(notes || []).filter(note => note.id !== noteId)}
            initial={{
              type: tagValue,
              fields: fieldsFromNote({ type: tagValue, message: comment, metadata }),
              intercom_id: metadata?.intercom_id || '',
              share_with_client: !!metadata?.shared,
              client_message: metadata?.client_message || '',
            }}
            className="u-border-top"
            onSubmit={updateSupportUserNote}
            onCancel={() => setIsEditing(false)}
            actionLabel={t('common.updateNote')}
          />
        ) : (
          <CommentBox
            ref={commentBoxRef}
            tags={tagOptions}
            tagDefault={tagValue}
            className="u-border-top"
            value={comment}
            maxLength={maxLength}
            onSubmit={({ message, tag }) => updateSupportUserNote({ message, type: tag })}
            onCancel={() => setIsEditing(false)}
            actionLabel={t('common.addNote')}
          />
        )
      ) : (
        <div className="d-flex flex-column u-gap-small">
          <span className="u-font-size-small secondary-text" style={{ whiteSpace: 'pre-wrap' }}>
            {comment}
          </span>
          {intercomUrl && (
            <a href={intercomUrl} target="_blank" rel="noreferrer" className="u-font-size-x-small">
              Open Intercom conversation
            </a>
          )}
        </div>
      )}
    </div>
  );
};
